var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { Link } from 'react-router-dom';
import PickupLocation from '../components/classified/PickupLocation';
import SalePointSelectField from '../components/CreateClassifiedForm/SalePointSelectField';
import useListMySalePoints from '../hooks/useListMySalePoints';
import { deleteClassified, listMyClassifieds, updateClassified } from '../utils/api/classifiedApi';
import { buildInitialEditValues, buildUpdateRequest, conditionOptions, selectableStatusOptions, statusLabels, } from '../utils/classifiedFormOptions';
var PAGE_SIZE = 50;
var STATUS_TABS = ['active', 'sold', 'inactive', 'outdated'];
var STATUS_TAB_LABELS = {
    active: 'aktive',
    sold: 'solgte',
    inactive: 'inaktive',
    outdated: 'utløpte',
};
var normalizeStatus = function (status) { return (status && STATUS_TABS.includes(status) ? status : 'active'); };
var ClassifiedRow = function (_a) {
    var _b;
    var classified = _a.classified, salePoints = _a.salePoints, onUpdated = _a.onUpdated, onDeleted = _a.onDeleted;
    var _c = React.useState(function () { return buildInitialEditValues(classified); }), values = _c[0], setValues = _c[1];
    var _d = React.useState(false), isSaving = _d[0], setIsSaving = _d[1];
    var _e = React.useState(null), error = _e[0], setError = _e[1];
    var handleUpdate = function () {
        setIsSaving(true);
        setError(null);
        updateClassified(classified.id, buildUpdateRequest(values))
            .then(function (next) {
            setValues(buildInitialEditValues(next));
            onUpdated(next);
        })
            .catch(function () { return setError('Kunne ikke lagre endringene. Prøv igjen.'); })
            .finally(function () { return setIsSaving(false); });
    };
    var handleDelete = function () {
        if (!window.confirm("Slette annonsen for \"" + classified.bookTitle + "\"?")) {
            return;
        }
        setIsSaving(true);
        setError(null);
        deleteClassified(classified.id)
            .then(function () { return onDeleted(classified.id); })
            .catch(function () {
            setError('Kunne ikke slette annonsen. Prøv igjen.');
            setIsSaving(false);
        });
    };
    return React.createElement("tr", { className: "object classified_row" },
        React.createElement("td", { className: "wide-column" },
            React.createElement("input", { name: "isbn_input", type: "text", value: (_b = classified.bookIsbn) !== null && _b !== void 0 ? _b : '', readOnly: true, "aria-label": "ISBN" }),
            React.createElement("p", null,
                React.createElement("a", { href: "/bok/" + classified.bookIsbn }, classified.bookTitle))),
        React.createElement("td", { className: "wide-column" },
            React.createElement(PickupLocation, { classified: classified }),
            React.createElement("select", { name: "condition_dropdown", "aria-label": "Tilstand", value: values.condition, disabled: isSaving, onChange: function (event) { return setValues(__assign(__assign({}, values), { condition: event.target.value })); } }, conditionOptions.map(function (option) { return (React.createElement("option", { key: option.value, value: option.value }, option.label)); })),
            React.createElement(SalePointSelectField, { title: "Salgssted", name: "salepoint_dropdown_" + classified.id, currentValue: values.salePoint, salePoints: salePoints, disabled: isSaving, onChange: function (event) { return setValues(__assign(__assign({}, values), { salePoint: event.target.value })); }, onBlur: function () { return undefined; } }),
            React.createElement("select", { name: "status_dropdown", "aria-label": "Status", value: values.status, disabled: isSaving, onChange: function (event) { return setValues(__assign(__assign({}, values), { status: event.target.value })); } }, selectableStatusOptions.map(function (option) { return (React.createElement("option", { key: option.value, value: option.value }, option.label)); }))),
        React.createElement("td", { className: "slim-column" },
            React.createElement("input", { name: "price_input", type: "number", min: 0, max: 9999, required: true, "aria-label": "Pris", value: values.price, disabled: isSaving, onChange: function (event) { return setValues(__assign(__assign({}, values), { price: event.target.value })); } })),
        React.createElement("td", null,
            error && React.createElement("p", { role: "alert", className: "form-error" }, error),
            React.createElement("div", { className: "horizontal nowrap" },
                React.createElement("button", { type: "button", className: "button", disabled: isSaving || values.price === '' || !values.salePoint, onClick: handleUpdate }, "Oppdater"),
                React.createElement("button", { type: "button", className: "button dangerous", disabled: isSaving, onClick: handleDelete }, "Slett"))));
};
/**
 * "Administrer annonser" — bulk management of the signed-in
 * user's classifieds, for users with a large amount of ads.
 * */
var AdministerClassifiedsPage = function (_a) {
    var status = _a.status;
    var salePoints = useListMySalePoints().salePoints;
    var selectedStatus = normalizeStatus(status);
    var _b = React.useState(null), classifieds = _b[0], setClassifieds = _b[1];
    var _c = React.useState(true), isLoading = _c[0], setIsLoading = _c[1];
    var _d = React.useState(null), error = _d[0], setError = _d[1];
    var _e = React.useState(''), search = _e[0], setSearch = _e[1];
    var _f = React.useState(1), page = _f[0], setPage = _f[1];
    React.useEffect(function () {
        var isMounted = true;
        setClassifieds(null);
        setIsLoading(true);
        setError(null);
        setPage(1);
        listMyClassifieds(selectedStatus)
            .then(function (nextClassifieds) {
            if (isMounted) {
                setClassifieds(nextClassifieds || []);
            }
        })
            .catch(function () {
            if (isMounted) {
                setError('Kunne ikke hente annonsene dine. Prøv igjen.');
            }
        })
            .finally(function () {
            if (isMounted) {
                setIsLoading(false);
            }
        });
        return function () {
            isMounted = false;
        };
    }, [selectedStatus]);
    var searchValue = search.trim().toLowerCase();
    var filteredClassifieds = (classifieds || []).filter(function (classified) { return (searchValue === ''
        || String(classified.bookIsbn || '').includes(searchValue)
        || (classified.bookTitle || '').toLowerCase().includes(searchValue)); });
    var totalPages = Math.max(1, Math.ceil(filteredClassifieds.length / PAGE_SIZE));
    var currentPage = Math.min(page, totalPages);
    var visibleClassifieds = filteredClassifieds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    var replaceClassified = function (next) {
        setClassifieds(function (current) { return ((current || []).map(function (classified) { return (classified.id === next.id ? next : classified); })); });
    };
    var removeClassified = function (classifiedId) {
        setClassifieds(function (current) { return (current || []).filter(function (classified) { return (classified.id !== classifiedId); }); });
    };
    return React.createElement("div", { className: "main-content" },
        React.createElement("h1", null, "Lag/Endre annonser"),
        React.createElement("div", { className: "admin" },
            React.createElement("div", { className: "columns" },
                React.createElement("span", null,
                    "Viser ",
                    STATUS_TAB_LABELS[selectedStatus],
                    " annonser",
                    ' ',
                    "(status: ",
                    statusLabels[selectedStatus],
                    ")."),
                React.createElement("span", { className: "float-right" },
                    "Vis heller:",
                    STATUS_TABS
                        .filter(function (tab) { return tab !== selectedStatus; })
                        .map(function (tab) { return (React.createElement(React.Fragment, { key: tab },
                        ' ',
                        React.createElement(Link, { to: "/administrer_annonser/" + tab }, STATUS_TAB_LABELS[tab]))); }))),
            React.createElement("div", { className: "columns" },
                React.createElement("form", { onSubmit: function (event) {
                        event.preventDefault();
                        setPage(1);
                    } },
                    React.createElement("input", { type: "search", name: "isbn", placeholder: "S\u00F8k ISBN eller tittel", value: search, onChange: function (event) {
                            setSearch(event.target.value);
                            setPage(1);
                        } }),
                    React.createElement("button", { type: "submit", className: "button z-1" }, "S\u00F8k")),
                React.createElement("a", { id: "add_button", className: "button z-1", href: "/sett_inn_annonse" }, "Legg til en ny annonse")),
            error && React.createElement("p", { role: "alert", className: "form-error" }, error),
            React.createElement("table", { id: "classified_table", className: "classified_list list" },
                React.createElement("thead", null,
                    React.createElement("tr", { className: "table-header" },
                        React.createElement("th", { className: "wide-column" }, "ISBN/Tittel"),
                        React.createElement("th", { className: "wide-column" }, "Status/Salgssted"),
                        React.createElement("th", { className: "slim-column" }, "Pris"),
                        React.createElement("th", null))),
                React.createElement("tbody", null,
                    isLoading && React.createElement("tr", null,
                        React.createElement("td", { colSpan: 4 }, "Laster annonsene dine...")),
                    !isLoading && visibleClassifieds.map(function (classified) { return (React.createElement(ClassifiedRow, { key: classified.id, classified: classified, salePoints: salePoints, onUpdated: replaceClassified, onDeleted: removeClassified })); }),
                    !isLoading && visibleClassifieds.length === 0 && React.createElement("tr", null,
                        React.createElement("td", { colSpan: 4 }, "Det er ingen annonser her.")))),
            React.createElement("div", { className: "columns" },
                React.createElement("span", null,
                    "Side ",
                    currentPage,
                    " av ",
                    totalPages,
                    ' ',
                    "(",
                    filteredClassifieds.length,
                    " annonser)"),
                React.createElement("span", { className: "float-right" },
                    currentPage > 1 && React.createElement("button", { type: "button", className: "button", onClick: function () { return setPage(currentPage - 1); } }, "Forrige side"),
                    currentPage < totalPages && React.createElement("button", { type: "button", className: "button", onClick: function () { return setPage(currentPage + 1); } }, "Neste side")))));
};
export default AdministerClassifiedsPage;
